<template>
  <div class="component">
    <Badge
      v-if="isConnected || newConnectionComplete"
      class="badge"
      status="success"
      iconBefore="fa fa-check"
    >
      Connected
    </Badge>

    <Badge
      v-else-if="connectionInProgress"
      class="badge"
      status="info"
      iconBefore="fa fa-spinner fa-spin"
    >
      Connecting
    </Badge>

    <Badge
      v-else-if="isConnected === undefined"
      class="badge"
      iconBefore="fa fa-spinner fa-spin"
      spacing="tight"
    >
      Checking Connection
    </Badge>

    <Button
      v-else
      @click="showModal = true"
      iconBefore="fa fa-plug"
      spacing="tight"
    >
      Connect
    </Button>

    <Button
      v-if="isConnected"
      @click="confirmDisconnect"
      iconBefore="fa fa-unlink"
      spacing="tight"
      variant="danger"
    >
      Disconnect
    </Button>

    <StakeModal v-model="showModal">
      <div class="tw-flex tw-flex-col tw-gap-10 tw-items-stretch tw-w-96">
        <h2 class="tw-text-center tw-text-lg tw-font-bold">
          Connect to {{ app.name }}
        </h2>
        <div class="tw-flex tw-flex-col tw-gap-3">
          <input
            v-model="username"
            class="input-base"
            type="text"
            :placeholder="`${ app.name } username`"
          >
          <input
            v-model="password"
            class="input-base"
            type="password"
            :placeholder="`${ app.name } password`"
          >
        </div>
        <button
          class="button-base tw-bg-sky-600 hover:tw-bg-sky-700 tw-text-gray-200 hover:tw-text-white tw-cursor-pointer"
          @click="connect"
        >
          <i :class="['fa', connectionInProgress ? 'fa-spinner fa-spin' : 'fa-plug']" />
          Connect{{ connectionInProgress ? 'ing' : '' }}
        </button>

        <StakeTooltip
          :label="tooltipLabel"
          position="bottom"
          tooltip-classes="tw-bg-sky-600 tw-text-white"
        >
          <p class="tw-text-sm tw-text-center tw-text-gray-500">
            Why do I have to type in my password
            <i class="fa fa-question-circle tw-text-sky-600" />
          </p>
        </StakeTooltip>
      </div>
    </StakeModal>
  </div>
</template>

<script>
import StakeModal from '@stakeui/StakeModal.vue';
import StakeTooltip from '@stakeui/StakeTooltip.vue';
import {
  Badge,
  Button,
  Buttons,
} from '@stakeui';

export default {
  name: 'OAuthConnect',
  components: {
    StakeModal,
    StakeTooltip,
    Badge,
    Button,
    Buttons,
  },
  props: {
    app: {
      type: Object,
      required: true,
    },
    isConnected: {
      type: Boolean,
      default: undefined,
    },
    code: {
      type: String,
      default: null,
    },
    authorizedApp: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      redirectBase: `${window.location.origin}/ui/advisor/integrations`,
      connectionInProgress: null,
      newConnectionComplete: null,
      showModal: false,
      username: null,
      password: null,
    };
  },
  computed: { /*
      Creates a new property
      Updates when any dependant property changes */
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    redirectURL() {
      if (!this.app.name) return this.redirectBase;
      return `${this.redirectBase}?app=${this.app.name}`;
    },
    authURL() {
      if (!this.app?.auth_url) return null;
      const url = new URL(this.app.auth_url);

      const params = {
        response_type: 'code',
        client_id: this.app.client_id,
        redirect_uri: this.redirectURL,
      };
      url.search = new URLSearchParams(params).toString();
      return url;
    },
    tooltipLabel() {
      return `
        This is due to how the ${this.app.name} system works.
        You only need to do this once.
        We never store your username or password.
        It's securely sent once to get your unique user key,
        which we keep safely encrypted.
      `;
    },
  },
  watch: { /*
        Watches an existing property
        Only runs when the watched property changes */
  },
  beforeCreate() {},
  created() {
    if (this.code && this.authorizedApp && this.app.name === this.authorizedApp) {
      this.connect();
    }
  },
  mounted() {},
  updated() {},
  methods: {
    async connect() {
      this.connectionInProgress = true;
      try {
        const res = await fetch('/api/v1/oauth/connect/', {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({
            app: this.app.name,
            username: this.username,
            password: this.password,
          }),
        });
        const data = await res.json();

        if (data.connected) {
          this.newConnectionComplete = true;
          this.$emit('update-connection', true);
        } else {
          alert('Failed to connect. Please check your credentials and try again.');
        }
      } catch (error) {
        console.error('Error connecting:', error);
        alert('An error occurred during the connection process. Please check your credentials and try again.');
      } finally {
        this.connectionInProgress = false;
        this.showModal = false;
      }
    },
    confirmDisconnect() {
      if (window.confirm('Are you sure you want to disconnect?')) {
        this.disconnect();
      }
    },
    async disconnect() {
      try {
        const res = await fetch('/api/v1/oauth/disconnect/', {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({ app_name: this.app.name }),
        });
        const data = await res.json();
        if (res.ok) {
          this.$emit('update-connection', false); // Emit event to update parent state
          this.newConnectionComplete = false;
          alert(data.message);
        } else {
          alert(data.error);
        }
      } catch (error) {
        console.error('Error disconnecting:', error);
      }
    },
  },
};
</script>

<style scoped>
.component {
  display: flex;
  align-items: end;
  gap: 1em;
  flex-grow: 1;
  justify-content: end;
  flex-direction: column;
}

.badge {
  margin-block-end: auto;
}
</style>
